import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Input } from "@/components/ui/Input/Input";
import { Modal } from "@/components/ui/Modal/Modal";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/lib/AuthContext";
import {
  useBrands,
  useDeleteWorkspace,
  useLeaveWorkspace,
  useUpdateWorkspace,
  useWorkspace,
} from "@/lib/queries";
import { PLANS } from "@notamen/core/billing";
import styles from "./Settings.module.css";

/** Human-readable plan name from the stored plan id, falling back gracefully. */
function planLabel(plan: string | undefined): string {
  if (!plan) return "—";
  const entry = (PLANS as Record<string, { name: string }>)[plan];
  return entry?.name ?? plan;
}

/** Pull a clean message off an unknown error, with a sensible fallback. */
function errMessage(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) {
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  return "Something went wrong. Please try again.";
}

/**
 * Workspace Settings — workspace-scoped account management (distinct from the
 * personal /account page). Renames the workspace (owner only), shows a
 * read-only info card, and a danger zone: delete the workspace (owner) or
 * leave it (any other member). Every control is enforced server-side; the UI
 * gating below just avoids showing affordances a caller can't use.
 */
export function Settings() {
  const { ws } = useParams<{ ws: string }>();
  const auth = useAuth();

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/settings`} replace />;
  }

  return (
    <AppShell currentWorkspaceId={ws}>
      <SettingsPage ws={ws} />
    </AppShell>
  );
}

function SettingsPage({ ws }: { ws: string | undefined }) {
  const navigate = useNavigate();
  const workspaceQ = useWorkspace(ws);
  const brandsQ = useBrands(ws);

  const workspace = workspaceQ.data?.workspace;
  const role = workspaceQ.data?.role;
  const isOwner = role === "owner";

  const brandCount = brandsQ.data?.brands.length ?? 0;

  // ── Rename (owner only) ──────────────────────────────────────────────
  const updateWs = useUpdateWorkspace(ws);
  const [name, setName] = useState("");
  const [nameDirty, setNameDirty] = useState(false);
  // Seed the field from the loaded workspace once, then let the user edit.
  const currentName = workspace?.name ?? "";
  const fieldValue = nameDirty ? name : currentName;
  const trimmed = fieldValue.trim();
  const nameChanged = trimmed.length > 0 && trimmed !== currentName;

  function onSaveName(e: React.FormEvent) {
    e.preventDefault();
    if (!nameChanged || trimmed.length > 80) return;
    updateWs.mutate(
      { name: trimmed },
      {
        onSuccess: () => {
          setNameDirty(false);
          setName("");
        },
      },
    );
  }

  // ── Danger zone ──────────────────────────────────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  if (workspaceQ.isLoading) {
    return (
      <div className={styles.page}>
        <p className={styles.muted}>Loading…</p>
      </div>
    );
  }

  if (workspaceQ.isError || !workspace) {
    return (
      <div className={styles.page}>
        <Btn
          href={`/w/${ws}`}
          variant="ghost"
          size="sm"
          iconLeft={<Icon name="arrow-left" size={13} />}
        >
          Back to workspace
        </Btn>
        <p className={styles.error}>
          <Icon name="alert" size={14} />{" "}
          {workspaceQ.error ? errMessage(workspaceQ.error) : "Workspace not found."}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Btn
        href={`/w/${ws}`}
        variant="ghost"
        size="sm"
        iconLeft={<Icon name="arrow-left" size={13} />}
      >
        Back to workspace
      </Btn>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className={styles.hero}>
        <span className={styles.chips}>
          <span className={styles.chip}>
            <Icon name="edit" size={12} /> Workspace settings
          </span>
        </span>
        <h1 className={styles.title}>
          <span className={styles.titleMuted}>Settings for</span>{" "}
          <b className={styles.titleAccent}>{workspace.name}</b>
          <span className={styles.titleMuted}>.</span>
        </h1>
        <p className={styles.lede}>
          Rename your workspace, review its plan, and manage your membership.
          Renaming and deleting are owner-only and enforced server-side.
        </p>
      </section>

      {/* ── Read-only info ───────────────────────────────────────── */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>Workspace</h2>
        <dl className={styles.infoGrid}>
          <div className={styles.infoRow}>
            <dt className={styles.infoLabel}>Name</dt>
            <dd className={styles.infoValue}>{workspace.name}</dd>
          </div>
          <div className={styles.infoRow}>
            <dt className={styles.infoLabel}>Plan</dt>
            <dd className={styles.infoValue}>{planLabel(workspace.plan)}</dd>
          </div>
          <div className={styles.infoRow}>
            <dt className={styles.infoLabel}>Your role</dt>
            <dd className={styles.infoValue}>{role ?? "—"}</dd>
          </div>
          {workspace.createdAt && (
            <div className={styles.infoRow}>
              <dt className={styles.infoLabel}>Created</dt>
              <dd className={styles.infoValue}>
                {new Date(workspace.createdAt).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </dd>
            </div>
          )}
          <div className={styles.infoRow}>
            <dt className={styles.infoLabel}>Workspace ID</dt>
            <dd className={`${styles.infoValue} ${styles.mono}`}>{workspace.id}</dd>
          </div>
        </dl>
        <p className={styles.cardHint}>
          Need more seats or a different plan?{" "}
          <a className={styles.inlineLink} href="/pricing">
            See plans &amp; billing →
          </a>
        </p>
      </section>

      {/* ── Rename (owner only) ──────────────────────────────────── */}
      {isOwner && (
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Rename workspace</h2>
          <p className={styles.cardLede}>
            The name appears in the workspace switcher and across the app.
          </p>
          <form className={styles.renameForm} onSubmit={onSaveName}>
            <Input
              value={fieldValue}
              onChange={(e) => {
                setNameDirty(true);
                setName(e.target.value);
              }}
              maxLength={80}
              aria-label="Workspace name"
              invalid={trimmed.length === 0 && nameDirty}
            />
            <Btn
              type="submit"
              variant="primary"
              loading={updateWs.isPending}
              disabled={!nameChanged || trimmed.length > 80}
            >
              Save
            </Btn>
          </form>
          {updateWs.isError && (
            <p className={styles.error}>
              <Icon name="alert" size={13} /> {errMessage(updateWs.error)}
            </p>
          )}
          {updateWs.isSuccess && !nameDirty && (
            <p className={styles.success}>
              <Icon name="check" size={13} /> Workspace renamed.
            </p>
          )}
        </section>
      )}

      {/* ── Danger zone ──────────────────────────────────────────── */}
      <section className={`${styles.card} ${styles.danger}`}>
        <h2 className={styles.cardTitle}>Danger zone</h2>
        {isOwner ? (
          <div className={styles.dangerRow}>
            <div>
              <div className={styles.dangerLabel}>Delete this workspace</div>
              <p className={styles.dangerHint}>
                {brandCount > 0
                  ? "Remove this workspace's brands first — they hold drafts, media, and scheduled posts."
                  : "Permanently removes the workspace and every member's access. This can't be undone."}
              </p>
            </div>
            <Btn
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
              disabled={brandCount > 0}
              iconLeft={<Icon name="alert" size={14} />}
            >
              Delete workspace
            </Btn>
          </div>
        ) : (
          <div className={styles.dangerRow}>
            <div>
              <div className={styles.dangerLabel}>Leave this workspace</div>
              <p className={styles.dangerHint}>
                You'll lose access until someone invites you back.
              </p>
            </div>
            <Btn
              variant="destructive"
              onClick={() => setLeaveOpen(true)}
              iconLeft={<Icon name="arrow-left" size={14} />}
            >
              Leave workspace
            </Btn>
          </div>
        )}
      </section>

      {isOwner && (
        <DeleteWorkspaceModal
          ws={ws}
          workspaceName={workspace.name}
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => navigate("/app")}
        />
      )}
      {!isOwner && (
        <LeaveWorkspaceModal
          ws={ws}
          workspaceName={workspace.name}
          open={leaveOpen}
          onClose={() => setLeaveOpen(false)}
          onLeft={() => navigate("/app")}
        />
      )}
    </div>
  );
}

/** Type-to-confirm delete. Requires the exact workspace name to arm. */
function DeleteWorkspaceModal({
  ws,
  workspaceName,
  open,
  onClose,
  onDeleted,
}: {
  ws: string | undefined;
  workspaceName: string;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const deleteWs = useDeleteWorkspace(ws);
  const [confirm, setConfirm] = useState("");
  const armed = confirm.trim() === workspaceName;

  function close() {
    deleteWs.reset();
    setConfirm("");
    onClose();
  }

  function onConfirm() {
    if (!armed) return;
    deleteWs.mutate(undefined, { onSuccess: onDeleted });
  }

  return (
    <Modal
      open={open}
      title="Delete workspace"
      onClose={close}
      footer={
        <>
          <Btn variant="ghost" onClick={close}>
            Cancel
          </Btn>
          <Btn
            variant="destructive"
            onClick={onConfirm}
            loading={deleteWs.isPending}
            disabled={!armed}
          >
            Delete forever
          </Btn>
        </>
      }
    >
      <p className={styles.modalBody}>
        This permanently deletes <b>{workspaceName}</b> and removes every
        member's access. This can't be undone.
      </p>
      <label className={styles.modalLabel}>
        Type <b>{workspaceName}</b> to confirm:
      </label>
      <Input
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        aria-label="Confirm workspace name"
        placeholder={workspaceName}
      />
      {deleteWs.isError && (
        <p className={styles.error}>
          <Icon name="alert" size={13} /> {errMessage(deleteWs.error)}
        </p>
      )}
    </Modal>
  );
}

/** Confirm leaving the workspace. */
function LeaveWorkspaceModal({
  ws,
  workspaceName,
  open,
  onClose,
  onLeft,
}: {
  ws: string | undefined;
  workspaceName: string;
  open: boolean;
  onClose: () => void;
  onLeft: () => void;
}) {
  const leaveWs = useLeaveWorkspace(ws);

  function close() {
    leaveWs.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      title="Leave workspace"
      onClose={close}
      footer={
        <>
          <Btn variant="ghost" onClick={close}>
            Cancel
          </Btn>
          <Btn
            variant="destructive"
            onClick={() => leaveWs.mutate(undefined, { onSuccess: onLeft })}
            loading={leaveWs.isPending}
          >
            Leave
          </Btn>
        </>
      }
    >
      <p className={styles.modalBody}>
        You'll lose access to <b>{workspaceName}</b> until someone invites you
        back.
      </p>
      {leaveWs.isError && (
        <p className={styles.error}>
          <Icon name="alert" size={13} /> {errMessage(leaveWs.error)}
        </p>
      )}
    </Modal>
  );
}
